import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Spinner,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { getZodFieldErrorMessages } from '../lib/zodErrorMessages';
import { Trans } from '@lingui/react/macro';
import { plural, t } from '@lingui/core/macro';
import { useCSVDownloader, useCSVReader } from 'react-papaparse';
import { TRPCClientError } from '@trpc/client';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { Select } from 'chakra-react-select';
import { SortOrder } from '@duke-hq/lib/pagination';
import { isDefined } from '@duke-hq/lib';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import { CourseItemStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseItemStatus.schema';
import type { QualificationKind } from '@duke-hq/svc-api-db/schemas/enums/QualificationKind.schema';
import { Download, Plus, ChevronDown, Upload, Table2, Code2, Wand2 } from 'lucide-react';
import { getCourseKinds, getQualificationKinds } from '../lib/courseUtils';
import { validateUrlField } from '../lib/validateUrlField';
import { CourseScheduleEditor } from './CourseScheduleEditor';
import { CourseSchedulePublishButton } from './CourseSchedulePublishButton';
import {
  buildDefaultScheduleData,
  normaliseUploadedScheduleRows,
  parseAdminCourseSchedule,
  toScheduleCsvRows,
} from './utils';
import { WorkPlacementRequirementsPanel } from './WorkPlacementRequirementsPanel';
import { CourseLanguagesField } from './CourseLanguagesField';
import { useCheckPermission } from '../hooks';
import type { UpdateCourseSettingsInput } from '@duke-hq/svc-api/admin/trpc/routers/course/mutations/updateSettings';

const DEFAULT_MAX_WEEKS = 53;

type ScheduleViewToggleProps = {
  scheduleValue: string;
  onChange: (json: string) => void;
  courseIdentifier?: string;
  courseScheduleVersionId: string | null;
  hasUnpublishedChanges: boolean;
  onPublishSuccess?: () => void;
  supersededUnitIds: Set<string>;
};

function ScheduleViewToggle({
  scheduleValue,
  onChange,
  courseIdentifier,
  courseScheduleVersionId,
  hasUnpublishedChanges,
  onPublishSuccess,
  supersededUnitIds,
}: ScheduleViewToggleProps) {
  const [viewMode, setViewMode] = useState<'editor' | 'json'>('editor');

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={2}>
        <FormLabel htmlFor="schedule" mb={2}>
          <Trans>Schedule</Trans>
        </FormLabel>
        <HStack spacing={2}>
          <HStack
            spacing={0}
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.default"
            display="inline-flex"
          >
            <Button
              size="xs"
              variant="ghost"
              borderRadius="0"
              borderRightRadius={viewMode === 'editor' ? 'md' : '0'}
              bg={viewMode === 'editor' ? 'bg.muted' : 'transparent'}
              onClick={() => setViewMode('editor')}
              px={2}
              minW="auto"
            >
              <Icon as={Table2} boxSize="3.5" />
            </Button>
            <Box w="1px" bg="border.default" />
            <Button
              size="xs"
              variant="ghost"
              borderRadius="0"
              borderLeftRadius={viewMode === 'json' ? 'md' : '0'}
              bg={viewMode === 'json' ? 'bg.muted' : 'transparent'}
              onClick={() => setViewMode('json')}
              px={2}
              minW="auto"
            >
              <Icon as={Code2} boxSize="3.5" />
            </Button>
          </HStack>
          <CourseSchedulePublishButton
            courseScheduleVersionId={courseScheduleVersionId}
            hasUnpublishedChanges={hasUnpublishedChanges}
            onPublishSuccess={onPublishSuccess}
          />
        </HStack>
      </Flex>

      {viewMode === 'editor' ? (
        <CourseScheduleEditor
          value={scheduleValue}
          onChange={onChange}
          courseIdentifier={courseIdentifier}
          supersededUnitIds={supersededUnitIds}
        />
      ) : (
        <Textarea
          resize="vertical"
          rows={24}
          size="xs"
          fontFamily="mono"
          spellCheck="false"
          value={scheduleValue}
          isReadOnly
        />
      )}
    </Box>
  );
}

type FormInputType = Omit<UpdateCourseSettingsInput, 'id'>;

export function CourseSettings() {
  const { courseId } = useParams();
  const [identifierMode, setIdentifierMode] = useState<'select' | 'custom'>('custom');
  const [updatedIdentifier, setUpdatedIdentifier] = useState<string | null>(null);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [autoScheduleModal, setAutoScheduleModal] = useState<{
    isOpen: boolean;
    maxWeeks: number;
  }>({ isOpen: false, maxWeeks: DEFAULT_MAX_WEEKS });

  if (!courseId) {
    throw new Error('courseId is required');
  }

  const { data: courses, refetch: refetchCourses } = trpcAdmin.courses.listCourses.useQuery({
    filters: {
      excludeEmptyIdentifier: true,
      accredited: true,
      isStream: false,
      kind: CourseKindSchema.enum.QUALIFICATION,
      orderBy: 'identifier',
      sort: SortOrder.ASC,
    },
  });

  const availableIdentifiers = useMemo(() => {
    const identifiers = new Set<string>();

    const filteredCourses =
      courses?.filter((course) => {
        const trimmedId = course.identifier?.trim();
        if (!trimmedId || identifiers.has(trimmedId)) {
          return false;
        }

        identifiers.add(trimmedId);
        return true;
      }) || [];

    return filteredCourses.map((course) => {
      return {
        value: course.identifier,
        label: course.identifier,
        title: course.title,
      };
    });
  }, [courses]);

  const availableKind = useMemo(() => getCourseKinds(), []);
  const availableQualificationKind = useMemo(() => getQualificationKinds(), []);

  const availableDerivedFromCourses = useMemo(() => {
    return (
      courses?.map((course) => ({
        value: course.id,
        label: `${course.identifier != null ? course.identifier + ' - ' : ''}${course.title}`,
      })) ?? []
    );
  }, [courses]);

  const {
    data: course,
    isLoading,
    refetch,
  } = trpcAdmin.courses.byId.useQuery(courseId, {
    enabled: false,
  });

  const workPlacementVisible = useCheckPermission('canManageWorkPlacements');
  const hasWorkPlacementUnits = useMemo(
    () => course?.courseItems.some((item) => item.unitOfCompetency?.work_placement) ?? false,
    [course?.courseItems],
  );
  const showWorkPlacementTab = workPlacementVisible && hasWorkPlacementUnits;

  const draftScheduleVersionId = useMemo(() => {
    return course?.courseScheduleVersions?.[0]?.id ?? null;
  }, [course?.courseScheduleVersions]);

  const hasUnpublishedScheduleChanges = useMemo(() => {
    const draftVersion = course?.courseScheduleVersions?.[0];
    if (!draftVersion?.updatedAt) {
      return false;
    }

    const lastPublishedAt = course?.lastPublishedScheduleVersion?.publishedAt;
    if (!lastPublishedAt) {
      return true;
    }

    return new Date(draftVersion.updatedAt) > new Date(lastPublishedAt);
  }, [course?.courseScheduleVersions, course?.lastPublishedScheduleVersion]);

  // Measure the sticky tab bar so the schedule column header can stick just beneath it, inside the
  // same scroll container — a sticky header rather than a second scroll area. This uses a callback
  // ref so measurement runs when the TabList actually mounts: the panel only renders once the
  // course has loaded, so an effect keyed on the ref would fire before the node exists and never
  // re-run to measure it.
  const [tabListHeight, setTabListHeight] = useState(0);
  const tabListObserver = useRef<ResizeObserver | null>(null);
  const tabListRef = useCallback((el: HTMLDivElement | null) => {
    tabListObserver.current?.disconnect();
    if (!el) {
      tabListObserver.current = null;
      return;
    }
    setTabListHeight(el.offsetHeight);
    tabListObserver.current = new ResizeObserver(() => setTabListHeight(el.offsetHeight));
    tabListObserver.current.observe(el);
  }, []);

  const toast = useToast();
  const { CSVDownloader } = useCSVDownloader();
  const { CSVReader } = useCSVReader();

  // Translation languages live in their own table, so they are edited as local
  // state here and persisted on the panel's Update alongside the course settings.
  const utils = trpcAdmin.useUtils();
  const { data: courseLanguagesData, isLoading: isLoadingLanguages } =
    trpcAdmin.contentTranslations.getCourseLanguages.useQuery({ courseId });
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const savedLanguages = useMemo(() => courseLanguagesData?.languages ?? [], [courseLanguagesData]);
  useEffect(() => {
    setSelectedLanguages(savedLanguages);
  }, [savedLanguages]);
  const setCourseLanguages = trpcAdmin.contentTranslations.setCourseLanguages.useMutation({
    onSuccess: () => {
      void utils.contentTranslations.getCourseLanguages.invalidate({ courseId });
      // Wake the coverage surfaces (toolbar pill, units-list badge, coverage
      // page): the server fanned out the newly-added languages before this
      // resolved, so the refetch shows them start translating without a reload.
      void utils.contentTranslations.listByCourse.invalidate({ courseId });
    },
    onError: () => {
      toast({
        title: t`Could not update the translation languages.`,
        status: 'error',
        isClosable: true,
      });
    },
  });

  const updateCourseSettings = trpcAdmin.courses.updateSettings.useMutation({
    // The success toast fires from onUpdate once the languages mutation has also
    // run, so a settings-ok/languages-failed save doesn't show both a success and
    // an error toast. This handler just resets the form to the saved values.
    onSuccess: (updatedCourse, variables) => {
      reset({
        identifier: updatedCourse.identifier ?? '',
        kind: updatedCourse.kind,
        qualificationKind:
          (updatedCourse.qualificationKind as QualificationKind | null | undefined) ?? undefined,
        title: updatedCourse.title,
        price_aud: parseFloat(updatedCourse.price_aud.toString()),
        description: updatedCourse.description ?? '',
        nominalHours: updatedCourse.nominalHours ?? 0,
        accredited: updatedCourse.accredited,
        schedule: variables.schedule,
        specialisation: updatedCourse.specialisation ?? '',
        specialisation_short: updatedCourse.specialisation_short ?? '',
        account_code: updatedCourse.account_code ?? '',
        isStream: updatedCourse.isStream,
        coachingCallLink: updatedCourse.coachingCallLink ?? undefined,
        courseFlyerLink: updatedCourse.courseFlyerLink ?? undefined,
        workPlacementGuideUrl: updatedCourse.workPlacementGuideUrl ?? undefined,
        expectedNumberOfHours: updatedCourse.expectedNumberOfHours ?? 0,
        collectGst: updatedCourse.collectGst ?? false,
        derivedFromCourseId: updatedCourse.derivedFromCourseId ?? undefined,
      });
    },
    onError: (error) => {
      let errorMessage = t`An error occurred while updating course settings.`;

      if (error instanceof TRPCClientError) {
        if (error.message) {
          errorMessage = error.message;
        }

        const fieldErrorMessages = getZodFieldErrorMessages(error).join(', ');
        if (fieldErrorMessages.length > 0) {
          errorMessage = fieldErrorMessages;
        }
      }

      const lines = errorMessage.split('\n');
      toast({
        title: t`Failed to update course settings.`,
        description: (
          <>
            {lines.map((line, i) => (
              <span key={line}>
                {line}
                {i < lines.length - 1 && <br />}
              </span>
            ))}
          </>
        ),
        status: 'error',
        isClosable: true,
        duration: 7000,
      });
    },
    onSettled: () => {
      void refetch();
      void refetchCourses();
    },
  });

  const {
    handleSubmit,
    register,
    clearErrors,
    getValues,
    setValue,
    watch,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormInputType>({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
    defaultValues: course
      ? {
          identifier: course.identifier ?? '',
          kind: course.kind,
          qualificationKind:
            (course.qualificationKind as QualificationKind | null | undefined) ?? undefined,
          title: course.title,
          price_aud: parseFloat(course.price_aud.toString()),
          description: course.description ?? '',
          nominalHours: course.nominalHours ?? undefined,
          accredited: course.accredited,
          schedule: JSON.stringify(course.schedule, null, 2),
          specialisation: course.specialisation ?? '',
          specialisation_short: course.specialisation_short ?? '',
          account_code: course.account_code ?? '',
          isStream: course.isStream ?? false,
          coachingCallLink: course.coachingCallLink ?? '',
          courseFlyerLink: course.courseFlyerLink ?? '',
          workPlacementGuideUrl: course.workPlacementGuideUrl ?? '',
          expectedNumberOfHours: course.expectedNumberOfHours ?? undefined,
          collectGst: course.collectGst ?? false,
          derivedFromCourseId: course.derivedFromCourseId ?? undefined,
        }
      : ({
          identifier: '',
          kind: CourseKindSchema.enum.QUALIFICATION,
          qualificationKind: undefined,
          title: '',
          price_aud: 0,
          description: '',
          nominalHours: 0,
          accredited: false,
          schedule: '',
          specialisation: '',
          specialisation_short: '',
          account_code: '',
          isStream: false,
          coachingCallLink: '',
          courseFlyerLink: '',
          workPlacementGuideUrl: '',
          expectedNumberOfHours: 0,
          collectGst: false,
          derivedFromCourseId: undefined,
        } satisfies FormInputType),
  });

  const [accredited, isStream, identifier, kind, price_aud] = watch([
    'accredited',
    'isStream',
    'identifier',
    'kind',
    'price_aud',
  ]);

  const onUpdate: SubmitHandler<FormInputType> = async (data) => {
    const updateData = {
      ...data,
      title: data.title.replace(/\s+/g, ' ').trim(),
      id: courseId,
      qualificationKind:
        data.kind === CourseKindSchema.enum.QUALIFICATION ? data.qualificationKind : null,
      derivedFromCourseId:
        data.kind === CourseKindSchema.enum.SHORT_COURSE ? data.derivedFromCourseId : null,
    };

    await updateCourseSettings.mutateAsync(updateData);

    // Persist languages alongside the settings, only when they changed so an
    // unrelated settings save skips the extra write.
    let translationsEnqueued = 0;
    const languagesChanged =
      selectedLanguages.length !== savedLanguages.length ||
      selectedLanguages.some((language) => !savedLanguages.includes(language));
    if (languagesChanged) {
      const result = await setCourseLanguages.mutateAsync({
        courseId,
        languages: selectedLanguages,
      });
      translationsEnqueued = result.translationKickoff?.translationsEnqueued ?? 0;
    }

    // Announce success only once both mutations have resolved, so a
    // settings-ok/languages-failed save shows just the error, not a false success.
    // When a newly-added language kicked off translations, say so, matching the
    // publish button's feedback.
    toast({
      title: t`Course settings updated successfully.`,
      description:
        translationsEnqueued > 0
          ? plural(translationsEnqueued, {
              one: 'Translating # translation. Track progress in the toolbar.',
              other: 'Translating # translations. Track progress in the toolbar.',
            })
          : undefined,
      status: 'success',
      isClosable: true,
    });
  };

  const onChangeSchedule = ({ data }: { data: Array<Record<string, string>> }) => {
    // Empty duration cells (e.g. unit rows) come back as durationWeeks: '' and would fail schema
    // validation, so drop them before persisting.
    const newSchedule = JSON.stringify(normaliseUploadedScheduleRows(data), null, 2);
    setValue('schedule', newSchedule);
  };

  const applyAutoScheduleWithWeeks = (maxWeeks: number) => {
    try {
      const scheduleValue = watch('schedule');
      if (scheduleValue === undefined) {
        return;
      }

      const scheduleItems = parseAdminCourseSchedule(scheduleValue);
      const newItems = [...scheduleItems];

      // Step 1: Set all LLN units to semester 0, week 1
      newItems.forEach((item, index) => {
        if (item['Unit Code'] === 'LLN') {
          newItems[index] = { ...item, Semester: '0', Week: '1' };
        }
      });

      // Step 2: Non LLN units
      const nonLlnIndices = newItems
        .map((item, index) => (item['Unit Code'] !== 'LLN' ? index : -1))
        .filter((index) => index !== -1);

      if (nonLlnIndices.length > 0) {
        // Find halfway point for semester assignment
        const halfwayPoint = Math.floor(nonLlnIndices.length / 2);

        // Step 3: Assign semesters
        nonLlnIndices.forEach((itemIndex, arrayIndex) => {
          const semester = arrayIndex >= halfwayPoint ? '2' : '1';
          const item = newItems[itemIndex];
          if (isDefined(item)) {
            newItems[itemIndex] = { ...item, Semester: semester };
          }
        });

        // Step 4: Assign weeks (spread across maxWeeks)
        const totalNonLlnItems = nonLlnIndices.length;

        if (totalNonLlnItems <= maxWeeks) {
          // One item per week (or less)
          nonLlnIndices.forEach((itemIndex, arrayIndex) => {
            const item = newItems[itemIndex];
            if (isDefined(item)) {
              newItems[itemIndex] = { ...item, Week: String(arrayIndex + 1) };
            }
          });
        } else {
          // Multiple items per week, try to distribute evenly
          const itemsPerWeek = totalNonLlnItems / maxWeeks;

          nonLlnIndices.forEach((itemIndex, arrayIndex) => {
            const week = Math.floor(arrayIndex / itemsPerWeek) + 1;
            const item = newItems[itemIndex];
            if (isDefined(item)) {
              newItems[itemIndex] = {
                ...item,
                Week: String(Math.min(week, maxWeeks)),
              };
            }
          });
        }
      }

      setValue('schedule', JSON.stringify(newItems, null, 2));
      setAutoScheduleModal({ isOpen: false, maxWeeks: DEFAULT_MAX_WEEKS });
    } catch {
      toast({
        title: t`Failed to apply auto schedule`,
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleAutoMagicSchedule = () => {
    setAutoScheduleModal({ isOpen: true, maxWeeks: DEFAULT_MAX_WEEKS });
  };

  const handleIdentifierModeChange = useCallback(() => {
    setIdentifierMode((prev) => {
      const nextMode = prev === 'select' ? 'custom' : 'select';

      if (nextMode === 'custom') {
        const isTaken = availableIdentifiers.some((i) => i.value === identifier);
        if (isTaken && identifier !== course?.identifier) {
          setShowAlert(isTaken);
        }
      } else {
        setShowAlert(false);
      }

      return nextMode;
    });
  }, [availableIdentifiers, identifier, course?.identifier]);

  const scheduleValue = watch('schedule');
  const currentScheduleData = useMemo(
    () => toScheduleCsvRows(parseAdminCourseSchedule(scheduleValue ?? '')),
    [scheduleValue],
  );

  const scheduleData = useMemo(
    () => buildDefaultScheduleData(course?.courseItems ?? []),
    [course?.courseItems],
  );

  const supersededUnitIds = useMemo(
    () =>
      new Set(
        (course?.courseItems ?? []).flatMap((item) =>
          item.status === CourseItemStatusSchema.enum.SUPERSEDED &&
          item.unit_of_competency_id !== null
            ? [item.unit_of_competency_id]
            : [],
        ),
      ),
    [course?.courseItems],
  );

  const courseTitle = useMemo(() => {
    const selectedCourse = courses?.find((option) => option.identifier === updatedIdentifier);
    return selectedCourse?.title;
  }, [updatedIdentifier, courses]);

  useEffect(() => {
    if (isStream) {
      setUpdatedIdentifier((prev) => {
        return prev !== (identifier ?? null) ? (identifier ?? null) : prev;
      });

      setShowAlert((prev) => {
        return prev !== isStream ? isStream : prev;
      });
    }
  }, [isStream, identifier]);

  if (isLoading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  if (!course) {
    return (
      <div>
        <Trans>No course settings found</Trans>
      </div>
    );
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onUpdate)} w="full" h="full" noValidate>
      <Stack spacing="1" h="full" w="full" justifyContent="space-between">
        {/* overflowY: auto makes overflow-x compute to auto too, clipping each
            field's focus ring (a box-shadow outside the border box) since the
            panels sit flush. A small horizontal gutter gives the ring room. The
            structural fix is tracked for the settings-drawer rethink. */}
        <Box
          h="full"
          overflowY="auto"
          px={1}
          sx={{ '--course-schedule-sticky-top': `${tabListHeight}px` }}
        >
          {/* Lazy tabs: only mount the visible panel. Without this, opening the
              drawer eagerly mounts the Schedule editor and Work Placement panel
              (each with their own data + heavy editors) even when the Details
              tab is showing, which is what made the drawer slow to open. */}
          <Tabs isFitted isLazy lazyBehavior="keepMounted">
            <TabList ref={tabListRef} position="sticky" top="0" zIndex="sticky" bg="bg.surface">
              <Tab>
                <Trans>Details</Trans>
              </Tab>
              <Tab>
                <Trans>Schedule</Trans>
              </Tab>
              <Tab>
                <Trans>Finance</Trans>
              </Tab>
              <Tab>
                <Trans>Translations</Trans>
              </Tab>
              {showWorkPlacementTab ? (
                <Tab>
                  <Trans>Work Placement</Trans>
                </Tab>
              ) : null}
            </TabList>

            <TabPanels py={4}>
              <TabPanel px={0}>
                <FormControl
                  id="identifier"
                  isInvalid={!!errors.identifier}
                  isRequired={accredited}
                >
                  <Flex justify="space-between" align="center" mb={1}>
                    <FormLabel htmlFor="identifier" mb={0}>
                      <Trans>Identifier / Code</Trans>
                    </FormLabel>
                    <Button
                      size="xs"
                      variant="link"
                      fontWeight="bold"
                      mb={0}
                      onClick={handleIdentifierModeChange}
                      leftIcon={
                        identifierMode === 'select' ? <Plus size={14} /> : <ChevronDown size={14} />
                      }
                    >
                      {identifierMode === 'select' ? t`Add/edit identifier` : t`Stream identifiers`}
                    </Button>
                  </Flex>

                  {identifierMode === 'custom' ? (
                    <Input
                      type="text"
                      autoComplete="off"
                      spellCheck="false"
                      {...register('identifier', {
                        validate: (value) => {
                          const trimmed = value?.trim();

                          if (accredited && !trimmed) {
                            return t`Course identifier is required when the course is marked as accredited.`;
                          }

                          if (trimmed && !/^[a-zA-Z0-9]+$/.test(trimmed)) {
                            return t`Only letters and numbers are allowed.`;
                          }

                          return true;
                        },
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          let value: string = e.target.value;

                          // Remove leading/trailing spaces *as the user types*
                          value = value.trim();

                          if (errors.identifier) {
                            clearErrors('identifier');
                          }

                          const isTaken =
                            identifierMode === 'custom' &&
                            value !== course.identifier &&
                            availableIdentifiers.some((i) => i.value === value);

                          setShowAlert(isTaken);
                          setUpdatedIdentifier(value);
                          setValue('identifier', value);
                          setValue('isStream', isTaken);
                        },
                      })}
                    />
                  ) : (
                    <Controller
                      name="identifier"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (accredited && !value) {
                            return t`Course identifier is required when the course is marked as accredited.`;
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <Select
                          placeholder={t`Please select`}
                          options={availableIdentifiers}
                          value={availableIdentifiers.find(
                            (option) => option.value === field.value,
                          )}
                          onChange={(selected) => {
                            if (selected) {
                              field.onChange(selected.value);

                              const currentValue = course?.identifier;
                              setUpdatedIdentifier(selected.value);

                              // Check if the selected identifier is different from the current one
                              const isTaken = selected.value !== currentValue;
                              setShowAlert(isTaken);
                              setValue('isStream', isTaken);
                            }
                          }}
                        />
                      )}
                    />
                  )}

                  <FormErrorMessage>{errors.identifier?.message}</FormErrorMessage>
                </FormControl>

                {showAlert ? (
                  <Alert status="warning" rounded="6" my="4">
                    <HStack>
                      <AlertIcon />
                    </HStack>
                    <AlertDescription>
                      <Text fontSize="sm">
                        <Trans>This course is a stream of</Trans> {courseTitle}
                      </Text>
                    </AlertDescription>
                  </Alert>
                ) : null}

                <FormControl isInvalid={!!errors.kind} isRequired>
                  <FormLabel htmlFor="state">
                    <Trans>Kind</Trans>
                  </FormLabel>
                  <Controller
                    name="kind"
                    control={control}
                    render={({ field }) => (
                      <Select
                        placeholder={t`Please select`}
                        options={availableKind}
                        value={availableKind.find((option) => option.value === field.value)}
                        onChange={(val: { value: string; label: string } | null) =>
                          field.onChange(val?.value)
                        }
                      />
                    )}
                    rules={{
                      required: t`Please select a course kind`,
                    }}
                  />
                  <FormErrorMessage>{errors.kind?.message}</FormErrorMessage>
                </FormControl>

                {kind === CourseKindSchema.enum.QUALIFICATION ? (
                  <FormControl isInvalid={!!errors.qualificationKind} isRequired>
                    <FormLabel htmlFor="qualificationKind">
                      <Trans>Qualification kind</Trans>
                    </FormLabel>
                    <Controller
                      name="qualificationKind"
                      control={control}
                      render={({ field }) => (
                        <Select
                          placeholder={t`Please select`}
                          options={availableQualificationKind}
                          value={availableQualificationKind.find(
                            (option) => option.value === field.value,
                          )}
                          onChange={(val: { value: string; label: string } | null) => {
                            field.onChange(val?.value);
                            if (val) {
                              clearErrors('qualificationKind');
                            }
                          }}
                        />
                      )}
                      rules={{
                        required:
                          kind === CourseKindSchema.enum.QUALIFICATION
                            ? t`Qualification kind is required when course kind is Qualification.`
                            : false,
                      }}
                    />
                    <FormErrorMessage>{errors.qualificationKind?.message}</FormErrorMessage>
                  </FormControl>
                ) : null}

                {kind === CourseKindSchema.enum.SHORT_COURSE ? (
                  <FormControl isInvalid={!!errors.derivedFromCourseId} isRequired>
                    <FormLabel htmlFor="derivedFromCourseId">
                      <Trans>Derived from course</Trans>
                    </FormLabel>
                    <Controller
                      name="derivedFromCourseId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          placeholder={t`Please select`}
                          options={availableDerivedFromCourses}
                          value={availableDerivedFromCourses.find(
                            (option) => option.value === field.value,
                          )}
                          onChange={(val: { value: string; label: string } | null) => {
                            field.onChange(val?.value);
                            if (val) {
                              clearErrors('derivedFromCourseId');
                            }
                          }}
                        />
                      )}
                      rules={{
                        required:
                          kind === CourseKindSchema.enum.SHORT_COURSE
                            ? t`Derived from course is required when course kind is Short Course.`
                            : false,
                      }}
                    />
                    <FormHelperText>
                      <Trans>
                        Select the accredited qualification this short course belongs to.
                      </Trans>
                    </FormHelperText>
                    <FormErrorMessage>{errors.derivedFromCourseId?.message}</FormErrorMessage>
                  </FormControl>
                ) : null}

                <FormControl isInvalid={!!errors.title} isRequired>
                  <FormLabel htmlFor="title">
                    <Trans>Title</Trans>
                  </FormLabel>
                  <Input
                    type="text"
                    autoComplete="off"
                    spellCheck="false"
                    {...register('title', {
                      required: t`Course title is required`,
                      validate: (value) => {
                        const normalized = value?.replace(/\s+/g, ' ').trim();
                        if (!normalized) {
                          return t`Course title cannot be empty or only spaces`;
                        }
                        return true;
                      },
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const cleaned: string = e.target.value.replace(/\s+/g, ' ');
                        setValue('title', cleaned, { shouldValidate: true });

                        if (errors.title) {
                          clearErrors('title');
                        }
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="specialisation">
                    <Trans>Specialisation</Trans>
                  </FormLabel>
                  <Input type="text" {...register('specialisation')} />
                </FormControl>

                <FormControl isInvalid={!!errors.nominalHours} isRequired={accredited}>
                  <FormLabel>
                    <Trans>Nominal hours</Trans>
                  </FormLabel>
                  <NumberInput
                    min={0}
                    defaultValue={getValues('nominalHours')}
                    onChange={(valueAsNumber) => {
                      setValue('nominalHours', parseFloat(valueAsNumber), {
                        shouldValidate: true,
                      });
                    }}
                  >
                    <NumberInputField
                      {...register('nominalHours', {
                        valueAsNumber: true,
                        validate: (value) => {
                          if (accredited && (typeof value !== 'number' || value <= 0)) {
                            return t`Nominal hours are required when the course is mark as accredited.`;
                          }
                          return true;
                        },
                      })}
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormErrorMessage>{errors.nominalHours?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.expectedNumberOfHours} isRequired={accredited}>
                  <FormLabel>
                    <Trans>Expected number of hours</Trans>
                  </FormLabel>
                  <NumberInput
                    min={0}
                    max={500}
                    step={2}
                    defaultValue={getValues('expectedNumberOfHours')}
                    onChange={(valueAsNumber) => {
                      const parsed = parseFloat(valueAsNumber);
                      if (!isNaN(parsed)) {
                        setValue('expectedNumberOfHours', parsed, {
                          shouldValidate: true,
                        });
                      }
                    }}
                  >
                    <NumberInputField
                      {...register('expectedNumberOfHours', {
                        valueAsNumber: true,
                        validate: (value) => {
                          if (typeof value !== 'number') {
                            return t`Expected number of hours must be a positive number.`;
                          }
                          if (value % 2 !== 0) {
                            return t`Expected number of hours must be an even number.`;
                          }
                          return true;
                        },
                      })}
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormErrorMessage>{errors.expectedNumberOfHours?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.coachingCallLink}>
                  <FormLabel htmlFor="coachingCallLink">
                    <Trans>Coaching call link</Trans>
                  </FormLabel>
                  <Input
                    type="text"
                    autoComplete="off"
                    spellCheck="false"
                    {...register('coachingCallLink', {
                      validate: validateUrlField,
                      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                        const raw: string = e.target.value.trim();

                        setValue('coachingCallLink', raw === '' ? null : raw, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.coachingCallLink?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.courseFlyerLink}>
                  <FormLabel htmlFor="courseFlyerLink">
                    <Trans>Course flyer link</Trans>
                  </FormLabel>
                  <Input
                    type="text"
                    autoComplete="off"
                    spellCheck="false"
                    {...register('courseFlyerLink', {
                      validate: validateUrlField,
                      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                        const raw: string = e.target.value.trim();

                        setValue('courseFlyerLink', raw === '' ? null : raw, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.courseFlyerLink?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.workPlacementGuideUrl}>
                  <FormLabel htmlFor="workPlacementGuideUrl">
                    <Trans>Work placement guide link</Trans>
                  </FormLabel>
                  <Input
                    type="text"
                    autoComplete="off"
                    spellCheck="false"
                    {...register('workPlacementGuideUrl', {
                      validate: validateUrlField,
                      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                        const raw: string = e.target.value.trim();

                        setValue('workPlacementGuideUrl', raw === '' ? null : raw, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.workPlacementGuideUrl?.message}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>
                    <Trans>Description</Trans>
                  </FormLabel>
                  <Textarea resize="vertical" rows={8} {...register('description')} />
                </FormControl>

                <FormControl>
                  <HStack justify="space-between" align="baseline" w="full" mt="1">
                    <FormLabel htmlFor="accredited">
                      <Trans>Accredited</Trans>
                    </FormLabel>
                    <Switch {...register('accredited')} colorScheme="brand" />
                  </HStack>
                </FormControl>
              </TabPanel>
              <TabPanel px={0}>
                <Stack spacing="4">
                  <Flex justify="space-between" align="center" w="full" gap={3}>
                    <HStack spacing={2}>
                      <CSVDownloader
                        filename={`${course.identifier}-default-schedule-${Date.now()}`}
                        bom={true}
                        config={{
                          delimiter: ';',
                        }}
                        data={scheduleData}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          leftIcon={<Icon as={Download} boxSize="4" />}
                        >
                          <Trans>Default</Trans>
                        </Button>
                      </CSVDownloader>
                      <CSVDownloader
                        filename={`${course.identifier}-current-schedule-${Date.now()}`}
                        bom={true}
                        config={{
                          delimiter: ';',
                        }}
                        data={currentScheduleData}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          leftIcon={<Icon as={Download} boxSize="4" />}
                        >
                          <Trans>Current</Trans>
                        </Button>
                      </CSVDownloader>
                    </HStack>
                    <HStack spacing={2}>
                      <Button
                        variant="outline"
                        size="sm"
                        colorScheme="purple"
                        leftIcon={<Icon as={Wand2} boxSize="4" />}
                        onClick={handleAutoMagicSchedule}
                      >
                        <Trans>Auto</Trans>
                      </Button>
                      <CSVReader
                        onUploadAccepted={onChangeSchedule}
                        noDrag
                        config={{ header: true }}
                      >
                        {({ getRootProps }) => (
                          <div {...getRootProps()}>
                            {
                              <Button
                                colorScheme="brand"
                                variant="outline"
                                size="sm"
                                leftIcon={<Icon as={Upload} boxSize="4" />}
                              >
                                <Trans>Upload</Trans>
                              </Button>
                            }
                          </div>
                        )}
                      </CSVReader>
                    </HStack>
                  </Flex>
                  <FormControl>
                    <ScheduleViewToggle
                      scheduleValue={watch('schedule') || ''}
                      onChange={(json) => setValue('schedule', json)}
                      courseIdentifier={course.identifier ?? undefined}
                      courseScheduleVersionId={draftScheduleVersionId}
                      hasUnpublishedChanges={hasUnpublishedScheduleChanges}
                      onPublishSuccess={() => void refetch()}
                      supersededUnitIds={supersededUnitIds}
                    />
                  </FormControl>
                </Stack>
              </TabPanel>
              <TabPanel px={0}>
                {price_aud === 0 ? (
                  <Alert status="warning" rounded="6" mb="4">
                    <HStack>
                      <AlertIcon />
                    </HStack>
                    <AlertDescription>
                      <Text fontSize="sm">
                        <Trans>
                          This course is set as free (price is $0). No invoices will be generated
                          for enrolments in this course.
                        </Trans>
                      </Text>
                    </AlertDescription>
                  </Alert>
                ) : null}
                <FormControl isInvalid={!!errors.price_aud}>
                  <FormLabel htmlFor="price_aud">
                    <Trans>Full course cost</Trans>
                  </FormLabel>
                  <NumberInput
                    min={0}
                    precision={2}
                    step={0.01}
                    onChange={(_, valueAsNumber) => {
                      setValue('price_aud', valueAsNumber, {
                        shouldValidate: true,
                      });
                    }}
                    defaultValue={getValues('price_aud')}
                  >
                    <NumberInputField
                      {...register('price_aud', {
                        valueAsNumber: true,
                        validate: (value) => {
                          if (value === undefined || isNaN(value)) {
                            return t`Price must be a valid number`;
                          }

                          if (value < 0) {
                            return t`Price cannot be less than 0`;
                          }

                          return true;
                        },
                      })}
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormErrorMessage>{errors.price_aud?.message}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="specialisation_short">
                    <Trans>Specialisation code</Trans>
                  </FormLabel>
                  <Input type="text" spellCheck="false" {...register('specialisation_short')} />
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="account_code">
                    <Trans>Account code</Trans>
                  </FormLabel>
                  <Input type="text" spellCheck="false" {...register('account_code')} />
                </FormControl>

                <FormControl>
                  <HStack justify="space-between" align="baseline" w="full" mt="1">
                    <FormLabel htmlFor="collectGst">
                      <Trans>Collect GST?</Trans>
                    </FormLabel>
                    <Controller
                      name="collectGst"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          isChecked={field.value}
                          onChange={(e) => {
                            field.onChange(e);
                          }}
                          colorScheme="brand"
                        />
                      )}
                    />
                  </HStack>
                </FormControl>
              </TabPanel>
              <TabPanel px={0}>
                <CourseLanguagesField
                  value={selectedLanguages}
                  onChange={setSelectedLanguages}
                  isLoading={isLoadingLanguages}
                />
              </TabPanel>
              {showWorkPlacementTab ? (
                <TabPanel px={0}>
                  <WorkPlacementRequirementsPanel courseId={courseId} />
                </TabPanel>
              ) : null}
            </TabPanels>
          </Tabs>
        </Box>

        {/* Right-aligned submit at the end of the form, matching the unit
            settings drawer (no separate footer panel). */}
        <Flex justify="flex-end" pt="4">
          <Button
            type="submit"
            colorScheme="brand"
            isLoading={isSubmitting}
            isDisabled={updateCourseSettings.isPending || isSubmitting}
            loadingText="Updating"
          >
            <Trans>Update</Trans>
          </Button>
        </Flex>
      </Stack>

      <Modal
        isOpen={autoScheduleModal.isOpen}
        onClose={() => setAutoScheduleModal({ isOpen: false, maxWeeks: DEFAULT_MAX_WEEKS })}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Auto Schedule</Trans>
          </ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel>
                <Trans>Number of weeks to distribute schedule across:</Trans>
              </FormLabel>
              <NumberInput
                min={1}
                max={200}
                value={autoScheduleModal.maxWeeks}
                onChange={(_, valueAsNumber) => {
                  setAutoScheduleModal((prev) => ({
                    ...prev,
                    maxWeeks: valueAsNumber,
                  }));
                }}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button
                variant="ghost"
                onClick={() =>
                  setAutoScheduleModal({
                    isOpen: false,
                    maxWeeks: DEFAULT_MAX_WEEKS,
                  })
                }
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                colorScheme="purple"
                onClick={() => applyAutoScheduleWithWeeks(autoScheduleModal.maxWeeks)}
              >
                <Trans>Apply</Trans>
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
